'use client';

import { css } from 'styled-components';
import { normalize } from 'styled-normalize';

import { baseTheme } from '.';
import type { BaseColorsType, BaseColorsTypeOld } from '../types';
import { baseDark } from './baseDark';
import { baseLight } from './baseLight';
import { Breakpoints, generateBreakpointsToken } from './breakpoints';
import type { Fonts} from './font';
import { generateFontTokens } from './font';
import type { Radius } from './radius';
import { generateRadiusTokens } from './radius';

type ColorsType =
  | {
      lightColors: BaseColorsType;
      darkColors: BaseColorsType;
      hasNewColorTheming: true;
    }
  | {
      lightColors: BaseColorsTypeOld;
      darkColors: BaseColorsTypeOld;
      hasNewColorTheming: false;
    };

export type CreateBaseThemeArgs = {
  radius: Radius;
  fonts: Fonts;
  aspectRatio: number;
} & ColorsType;

function hexToRgb(hex: string) {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
}

/*
  @deprecated
  this is a temporary function to get the tailwind color variables. remove after migration to tailwind
*/
const getTailwindColorVariables = (colors: Record<string, string>) => {
  return Object.keys(colors).reduce((acc: Record<string, string>, key) => {
    acc['--c-' + key] = hexToRgb(colors[key]);
    return acc;
  }, {});
};

export const createBaseTheme = (args: CreateBaseThemeArgs) => css`
  ${normalize}
  * {
    box-sizing: border-box;
  }

  :root {
    ${generateRadiusTokens(args.radius)}
    ${generateFontTokens(args.fonts)}
    ${baseTheme.aspectRatio.primary}: ${args.aspectRatio};
    ${generateBreakpointsToken(Breakpoints)}
  }

  [data-theme='light'] {
    ${baseLight(
      args.hasNewColorTheming
        ? {
            colors: args.lightColors,
            hasNewColorTheming: true,
          }
        : {
            colors: args.lightColors,
            hasNewColorTheming: false,
          },
    )}
    ${getTailwindColorVariables(args.lightColors)}
  }

  [data-theme='dark'] {
    ${baseDark(
      args.hasNewColorTheming
        ? {
            colors: args.darkColors,
            hasNewColorTheming: true,
          }
        : {
            colors: args.darkColors,
            hasNewColorTheming: false,
          },
    )}
    ${getTailwindColorVariables(args.darkColors)}
  }

  html,
  body {
    font-size: 10px;
    direction: rtl;
    margin: 0;
    padding: 0;
    font-family: var(${baseTheme.fonts.primary}) !important;
  }

  .fa {
    font-feature-settings: 'ss01';
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: var(${baseTheme.grey3}) var(${baseTheme.white});
  }

  *::-webkit-scrollbar-thumb {
    border-radius: var(${baseTheme.radius.full});
    background: var(${baseTheme.grey3});
  }

  .ReactQueryDevtools {
    direction: ltr;
  }
`;
